import { matchPath } from "react-router";
import { appRoutes } from "@/app/router";
import { AppStore } from "@/store";
import {
  UNTITLED_COLLECTION_TITLE,
  UNTITLED_NOTE_TITLE,
  UNTITLED_TEMPLATE_TITLE,
} from "@/domains/untitled/untitled";

/**
 * Derives a meaningful title from a path by extracting IDs and looking up
 * actual entity titles from the store, falling back to path-based names.
 */
export const getPathTitle = (path: string, store: AppStore): string => {
  // Try to extract note ID from path and get the actual note title
  const noteIdMatch = matchPath(
    appRoutes.notesView({ params: { noteId: ":noteId" }, starPattern: true }),
    path
  );

  if (noteIdMatch?.params.noteId) {
    const note = store.notes.get(noteIdMatch.params.noteId);
    if (note?.suggestionData?.suggested_primary_label)
      return note.suggestionData.suggested_primary_label;
    if (note) return note.title || UNTITLED_NOTE_TITLE;
  }

  // Try to extract collection ID from path and get the actual collection title
  const collectionIdMatch = matchPath(
    appRoutes.collectionsView({ params: { collectionId: ":collectionId" }, starPattern: true }),
    path
  );

  if (collectionIdMatch?.params.collectionId) {
    const collection = store.collections.get(collectionIdMatch.params.collectionId);
    if (collection) return collection.title || UNTITLED_COLLECTION_TITLE;
  }

  // Try to extract template ID from path and get the actual template title
  const templateIdMatch = matchPath(
    appRoutes.templatesView({ params: { templateId: ":templateId" }, starPattern: true }),
    path
  );

  if (templateIdMatch?.params.templateId) {
    const template = store.templates.get(templateIdMatch.params.templateId);
    if (template) return template.title || UNTITLED_TEMPLATE_TITLE;
  }

  // Try to extract session ID from path and get the actual session title
  const sessionIdMatch = matchPath(
    appRoutes.sessionsView({ params: { sessionId: ":sessionId" }, starPattern: true }),
    path
  );

  if (sessionIdMatch?.params.sessionId) {
    const session = store.sessions.get(sessionIdMatch.params.sessionId);
    if (session?.title) return session.title;
  }

  // Try to extract topic ID from path and get the actual topic title
  const topicIdMatch = matchPath(
    appRoutes.exploreTopicDetail({ params: { topicId: ":topicId" }, starPattern: true }),
    path
  );

  if (topicIdMatch?.params.topicId) {
    return "Explore Topic";
  }

  const chatConvoSessionIdMatch = matchPath(
    appRoutes.chatConversation({
      params: { sessionId: ":sessionId" },
      starPattern: true,
    }),
    path
  );

  if (chatConvoSessionIdMatch?.params.sessionId) {
    return "Chat Session";
  }

  // Handle search pages - show the query string if available
  if (path.startsWith(appRoutes.search({}).path)) {
    try {
      // Parse the search query from the path without using window
      const searchParams = new URLSearchParams(path.split("?")[1] || "");
      const queryString = searchParams.get("q") || searchParams.get("query");
      if (queryString) {
        return `Search: ${queryString}`;
      }
    } catch {
      // If parsing fails, fall back to simple path checking
    }
    return "Search";
  }

  // Handle saved search pages - try to get the saved search title
  if (path.startsWith(appRoutes.search({}).path) && path.includes("?")) {
    try {
      // This is a saved search, try to find it by the query string
      const searchQuery = path.split("?")[1] || "";

      // Look for a saved search with this query
      const savedSearch = store.savedSearches.all.find(
        saved => saved.savedSearchQueryString === `?${searchQuery}`
      );

      if (savedSearch?.title) {
        return savedSearch.title;
      }
    } catch {
      // If parsing fails, fall back to simple path checking
    }
  }

  // Handle specific page types with exact path matching
  if (path === appRoutes.notesList({}).path) return "Notes";
  if (path === appRoutes.collections({}).path) return "Collections";
  if (path === appRoutes.templatesList({}).path) return "Templates";
  if (path === appRoutes.sessionsList({}).path) return "Sessions";
  if (path === appRoutes.trash({}).path) return "Trash";
  if (path === appRoutes.explore({}).path) return "Explore";
  if (path === appRoutes.oauthConsent({}).path) return "OAuth Consent";

  // Handle audio recording routes
  if (path === appRoutes.audioRecordings({}).path) return "Audio Recordings";
  if (path === appRoutes.audioRecordingCreate({}).path) return "New Audio Recording";
  const audioRecordingMatch = matchPath(
    appRoutes.audioRecording({ params: { recordingId: ":recordingId" }, starPattern: true }),
    path
  );
  if (audioRecordingMatch?.params.recordingId) {
    return `Recording ${audioRecordingMatch.params.recordingId}`;
  }

  // Handle admin routes
  if (path.startsWith(appRoutes.admin({}).path)) return "Admin";

  // Handle debug routes
  if (path === appRoutes.debugDbRepl({}).path) return "DB REPL";
  if (path === appRoutes.debugDb({}).path) return "Debug DB";
  if (path.startsWith(appRoutes.debug({}).path)) return "Debug";

  // Handle settings routes
  if (path.startsWith(appRoutes.settings({}).path)) return "Settings";

  // Handle integrations routes
  if (path.startsWith(appRoutes.integrations({}).path)) return "Integrations";

  // Handle V1 migration routes using route helpers
  if (matchPath(appRoutes.v1MigrationGroups({ starPattern: true }), path))
    return "V1 Migration - Groups";
  if (matchPath(appRoutes.v1MigrationNotes({ starPattern: true }), path))
    return "V1 Migration - Notes";
  if (matchPath(appRoutes.v1MigrationTemplates({ starPattern: true }), path))
    return "V1 Migration - Templates";

  // Handle import batch routes using route helpers
  if (
    matchPath(
      appRoutes.importBatchNotes({
        params: { importBatchId: ":importBatchId" },
        starPattern: true,
      }),
      path
    )
  ) {
    return "Import Batch - Notes";
  }

  if (
    matchPath(
      appRoutes.importBatchCollections({
        params: { importBatchId: ":importBatchId" },
        starPattern: true,
      }),
      path
    )
  ) {
    return "Import Batch - Collections";
  }

  // Handle home and everything routes (redirects to notes)
  if (path === appRoutes.home({}).path || path === appRoutes.everything({}).path) return "Notes";

  return "Not Found";
};
